import React, { useCallback, useEffect, useState } from 'react';
import { customReportsAPI } from '../../services/customReportsService';
import { formatCellValue, downloadCustomReportCsv, printCustomReport } from './customReportExportUtils';
import { formatDateIN, formatDateTimeIN } from '../../utils/dateFormat';
import { IconBookmark, IconLayout } from '../../components/builderView/BuilderViewIcons';
import './customReportBuilder.css';

const visibilityBadgeClass = (v) => {
  if (v === 'PROJECT') return 'bv-badge--project';
  if (v === 'COMPANY') return 'bv-badge--company';
  return 'bv-badge--private';
};

const SavedCustomReports = ({ onEdit, projectFilter }) => {
  const [reports, setReports] = useState([]);
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [runResult, setRunResult] = useState(null);
  const [error, setError] = useState('');
  const [renameId, setRenameId] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const loadReports = useCallback(async () => {
    const params = {};
    if (search.trim()) params.search = search.trim();
    if (projectFilter && projectFilter !== 'all') params.projectId = projectFilter;
    const res = await customReportsAPI.list(params);
    setReports(res.data?.data || []);
  }, [search, projectFilter]);

  useEffect(() => { loadReports().catch(() => setError('Unable to load saved reports.')); }, [loadReports]);

  const handleRun = async (report) => {
    setBusyId(report.id);
    setError('');
    try {
      const body = {};
      if (projectFilter && projectFilter !== 'all') body.projectId = Number(projectFilter);
      const res = await customReportsAPI.run(report.id, body);
      setRunResult(res.data);
      await loadReports();
    } catch (e) {
      setError(e.response?.data?.message || 'Run failed.');
    } finally {
      setBusyId(null);
    }
  };

  const handleDuplicate = async (report) => {
    setBusyId(report.id);
    try {
      await customReportsAPI.duplicate(report.id);
      await loadReports();
    } catch (e) {
      setError(e.response?.data?.message || 'Duplicate failed.');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (report) => {
    if (!window.confirm(`Deactivate report "${report.reportName}"?`)) return;
    setBusyId(report.id);
    try {
      await customReportsAPI.remove(report.id);
      if (runResult?.report?.id === report.id) setRunResult(null);
      await loadReports();
    } catch (e) {
      setError(e.response?.data?.message || 'Delete failed.');
    } finally {
      setBusyId(null);
    }
  };

  const handleRename = async () => {
    if (!renameId || !renameValue.trim()) return;
    setBusyId(renameId);
    try {
      const existing = reports.find((r) => r.id === renameId);
      await customReportsAPI.update(renameId, { ...existing, reportName: renameValue.trim() });
      setRenameId(null);
      setRenameValue('');
      await loadReports();
    } catch (e) {
      setError(e.response?.data?.message || 'Rename failed.');
    } finally {
      setBusyId(null);
    }
  };

  const handleExportRun = () => {
    if (!runResult) return;
    downloadCustomReportCsv(runResult.columns, runResult.rows, `${runResult.report?.reportName || 'report'}.csv`);
  };

  const handlePrintRun = () => {
    if (!runResult) return;
    printCustomReport({
      title: runResult.report?.reportName,
      subtitle: runResult.report?.datasetKey,
      columns: runResult.columns,
      rows: runResult.rows,
      totals: runResult.totals,
    });
  };

  return (
    <div className="bv-shell">
      <div className="bv-header-row">
        <div className="bv-header">
          <h3>Saved custom reports</h3>
          <p>Run, edit, duplicate, or export reports saved from the builder.</p>
        </div>
        <input
          className="bv-search-input"
          placeholder="Search reports…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search saved reports"
        />
      </div>

      {error && <div className="bv-alert bv-alert--error">{error}</div>}

      {reports.length === 0 ? (
        <div className="bv-empty-state">
          <IconBookmark />
          <p>No saved custom reports yet. Build a report in the <strong>Custom Report Builder</strong> tab and save it.</p>
        </div>
      ) : (
        <div className="bv-item-list">
          {reports.map((r) => (
            <div key={r.id} className={`bv-item-card ${runResult?.report?.id === r.id ? 'is-selected' : ''}`}>
              <div className="bv-item-card__meta">
                <div className="bv-item-card__name">
                  {r.reportName}
                  <span className={`bv-badge ${visibilityBadgeClass(r.visibility)}`}>{r.visibility}</span>
                </div>
                <div className="bv-item-card__desc">
                  <IconLayout />
                  {' '}{r.datasetKey}
                  {r.reportFolder ? ` · Folder: ${r.reportFolder}` : ''}
                  {r.project?.name ? ` · Project: ${r.project.name}` : ''}
                  {r.createdBy?.name ? ` · By ${r.createdBy.name}` : ''}
                  {r.lastRunAt ? ` · Last run ${formatDateTimeIN(r.lastRunAt)}` : ' · Never run'}
                  {` · Created ${formatDateIN(r.createdAt)}`}
                </div>
              </div>
              <div className="bv-item-card__actions">
                <button type="button" className="bv-item-action" disabled={busyId === r.id} onClick={() => handleRun(r)}>
                  {busyId === r.id ? 'Running…' : 'Run'}
                </button>
                <button type="button" className="bv-item-action" onClick={() => onEdit?.(r.id)}>Edit</button>
                <button type="button" className="bv-item-action" disabled={busyId === r.id} onClick={() => handleDuplicate(r)}>Duplicate</button>
                <button type="button" className="bv-item-action" onClick={() => { setRenameId(r.id); setRenameValue(r.reportName); }}>Rename</button>
                <button type="button" className="bv-item-action bv-item-action--danger" disabled={busyId === r.id} onClick={() => handleDelete(r)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {renameId && (
        <div className="modal-overlay bv-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Rename report</h2>
              <button type="button" className="btn-close" onClick={() => setRenameId(null)} aria-label="Close">
                <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="bv-form-group">
                <label className="bv-form-label" htmlFor="scr-rename">Report name</label>
                <input id="scr-rename" value={renameValue} onChange={(e) => setRenameValue(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleRename()} />
              </div>
            </div>
            <div className="modal-footer bv-modal-footer">
              <button type="button" className="btn-secondary" onClick={() => setRenameId(null)}>Cancel</button>
              <button type="button" className="btn-primary" disabled={busyId === renameId} onClick={handleRename}>Save</button>
            </div>
          </div>
        </div>
      )}

      {runResult && (
        <div className="bv-preview">
          <div className="bv-preview__head">
            <div>
              <h4>{runResult.report?.reportName}</h4>
              <p className="bv-preview__meta">
                Dataset: {runResult.report?.datasetKey} · Rows: {runResult.totalCount}
                {runResult.report?.lastRunAt ? ` · Last run: ${formatDateTimeIN(runResult.report.lastRunAt)}` : ''}
              </p>
            </div>
            <div className="bv-toolbar-actions">
              <button type="button" className="bv-btn-outline" onClick={() => onEdit?.(runResult.report?.id)}>Edit report</button>
              <button type="button" className="bv-btn-outline" onClick={handleExportRun}>Export CSV</button>
              <button type="button" className="bv-btn-outline" onClick={handlePrintRun}>Print</button>
              <button type="button" className="bv-item-action" onClick={() => setRunResult(null)}>Close</button>
            </div>
          </div>
          <div className="bv-preview__body">
            <table className="data-table">
              <thead><tr>{runResult.columns?.map((c) => <th key={c.key}>{c.label}</th>)}</tr></thead>
              <tbody>
                {runResult.rows?.map((row, i) => (
                  <tr key={i}>{runResult.columns.map((c) => <td key={c.key}>{formatCellValue(row[c.key], c.type)}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default SavedCustomReports;
